"use client";

import {cn} from "@/lib/utils";
import {memo, useMemo, type CSSProperties} from "react";
import type {ThemedToken} from "shiki";
import type {TokenizedCode} from "./code-block-highlight";

// Shiki uses bitflags for font styles: 1=italic, 2=bold, 4=underline
// oxlint-disable-next-line eslint(no-bitwise)
const isItalic = (fontStyle: number | undefined) => fontStyle && fontStyle & 1;
// oxlint-disable-next-line eslint(no-bitwise)
const isBold = (fontStyle: number | undefined) => fontStyle && fontStyle & 2;
const isUnderline = (fontStyle: number | undefined) =>
   // oxlint-disable-next-line eslint(no-bitwise)
   fontStyle && fontStyle & 4;

interface KeyedToken {
   token: ThemedToken;
   key: string;
}
interface KeyedLine {
   tokens: KeyedToken[];
   key: string;
}

const addKeysToTokens = (lines: ThemedToken[][]): KeyedLine[] =>
   lines.map((line, lineIdx) => ({
      key: `line-${lineIdx}`,
      tokens: line.map((token, tokenIdx) => ({
         key: `line-${lineIdx}-${tokenIdx}`,
         token,
      })),
   }));

const TokenSpan = ({token}: {token: ThemedToken}) => (
   <span
      className="dark:!bg-[var(--shiki-dark-bg)] dark:!text-[var(--shiki-dark)]"
      style={
         {
            backgroundColor: token.bgColor,
            color: token.color,
            fontStyle: isItalic(token.fontStyle) ? "italic" : undefined,
            fontWeight: isBold(token.fontStyle) ? "bold" : undefined,
            textDecoration: isUnderline(token.fontStyle) ? "underline" : undefined,
            ...token.htmlStyle,
         } as CSSProperties
      }
   >
      {token.content}
   </span>
);

const LINE_NUMBER_CLASSES = cn(
   "block",
   "before:content-[counter(line)]",
   "before:inline-block",
   "before:[counter-increment:line]",
   "before:w-8",
   "before:mr-4",
   "before:text-right",
   "before:text-400/60",
   "before:font-mono",
   "before:select-none"
);

const LineSpan = ({
   keyedLine,
   showLineNumbers,
}: {
   keyedLine: KeyedLine;
   showLineNumbers: boolean;
}) => (
   <span className={showLineNumbers ? LINE_NUMBER_CLASSES : "block"}>
      {keyedLine.tokens.length === 0
         ? "\n"
         : keyedLine.tokens.map(({token, key}) => (
              <TokenSpan key={key} token={token} />
           ))}
   </span>
);

export const CodeBlockBody = memo(
   ({
      tokenized,
      showLineNumbers,
      className,
   }: {
      tokenized: TokenizedCode;
      showLineNumbers: boolean;
      className?: string;
   }) => {
      const keyedLines = useMemo(
         () => addKeysToTokens(tokenized.tokens),
         [tokenized.tokens]
      );

      return (
         <pre className={cn("m-0 p-3 text-xs leading-5", className)}>
            <code
               className={cn(
                  "font-mono",
                  showLineNumbers && "[counter-increment:line_0] [counter-reset:line]"
               )}
            >
               {keyedLines.map((keyedLine) => (
                  <LineSpan
                     key={keyedLine.key}
                     keyedLine={keyedLine}
                     showLineNumbers={showLineNumbers}
                  />
               ))}
            </code>
         </pre>
      );
   },
   (prevProps, nextProps) =>
      prevProps.tokenized === nextProps.tokenized &&
      prevProps.showLineNumbers === nextProps.showLineNumbers &&
      prevProps.className === nextProps.className
);

CodeBlockBody.displayName = "CodeBlockBody";
